import Sidebar from "../../components/Sidebar/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import "katex/dist/katex.min.css";
import Latex from "react-latex-next";
import { useEffect, useState } from "react";
// NOTE: uses scss from AssignmentPage component
import "../AssignmentPage/AssignmentPage.scss";
import axiosClient from "../../axiosClient";
import { Question, StarredQuestion } from "../../types";
import { ZoomImage } from "../../components/ZoomImage/ZoomImage";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { ImCross } from "react-icons/im";
import PracticePageSidebar from "./PracticePageSidebar";
import { AssignmentPageTitle } from "../AssignmentPage/AssignmentPageTitle";
import toast from "react-hot-toast";
import { Button, Modal } from "react-bootstrap";
import { SjtPracticeBlock } from "../../components/SJT/SjtPracticeBlock";
import { SjtOrderPanel } from "../../components/SJT/SjtOrderPanel";
import { computeSjtCorrect, getDefaultSjtOrderIds } from "../../components/SJT/sjtUtils";
import { McqPracticeBlock } from "../../components/MCQ/McqPracticeBlock";
import { RolePlayPracticeBlock } from "../../components/RolePlay/RolePlayPracticeBlock";
import { toggleStarQuestion } from "../../utils/starQuestion";
import { shouldIgnoreQuestionNavigationShortcut } from "../../utils/shouldIgnoreQuestionNavigationShortcut";
import { BsLightbulbFill } from "react-icons/bs";

const PracticePage = () => {

  const STARRED_BATCH_SIZE = 10;
  const MAX_SKIPPED_QUESTIONS = 50;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [didFailToLoadQuestions, setDidFailToLoadQuestions] = useState<boolean>(false);
  const [showCompletionModal, setShowCompletionModal] = useState<boolean>(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [submittedQuestionIds, setSubmittedQuestionIds] = useState<Set<number>>(
    new Set(),
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  // selectedAnswers maps each questionId to the answerId chosen
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: number;
  }>({});
  // For SJT questions, track ordered answer option ids by question id
  const [sjtOrderedAnswers, setSjtOrderedAnswers] = useState<{
    [key: number]: number[];
  }>({});
  // For RolePlay questions, track if answered correctly
  const [rolePlayAnswers, setRolePlayAnswers] = useState<{
    [key: number]: boolean;
  }>({});
  // For RolePlay questions, track mark scheme visibility
  const [rolePlayShowMarkScheme, setRolePlayShowMarkScheme] = useState<{
    [key: number]: boolean;
  }>({});

  // only used in practice mode
  const [practiceQuestionAnswered, setPracticeQuestionAnswered] =
    useState<boolean>(false);
  const [
    numberOfRemainingPracticeQuestionsInModule,
    setnumberOfRemainingPracticeQuestionsInModule,
  ] = useState<number>(0);
  const [practiceQuestionsAnsweredCount, setPracticeQuestionsAnsweredCount] =
    useState(0);
  const [showLatestQuestionInSidebar, setShowLatestQuestionInSidebar] =
    useState<boolean>(true);
  // Track whether we've already prefetched for the current loaded length to avoid duplicate calls
  const [prefetchedForLength, setPrefetchedForLength] = useState<number | null>(null);
  const [isFetchingNext, setIsFetchingNext] = useState<boolean>(false);
  const navigate = useNavigate();
  const data = useLocation();

  const getAssignmentTitle = () => data?.state?.assignmentName;
  const getPracticeModuleIds = () => data?.state?.practiceModuleIds;
  const getPracticeSubjectId = () => data?.state?.practiceSubjectId;
  const getExamBoards = (): string[] => data?.state?.examBoards;
  const getIsStarredMode = () => data?.state?.starred;
  const [highestQuestionNumberSeen, setHighestQuestionNumberSeen] =
    useState<number>(1);

  const questionNumber = currentQuestionIndex + 1;
  const isAtLastQuestionInModule =
    questionNumber === numberOfRemainingPracticeQuestionsInModule;

  const getUnansweredQuestionCount = () =>
    questions.filter((q) => !submittedQuestionIds.has(q.id)).length;

  const cannotLoadMoreQuestions = () => {
    if (getIsStarredMode()) {
      return questions.length >= MAX_SKIPPED_QUESTIONS;
    }
    return getUnansweredQuestionCount() >= MAX_SKIPPED_QUESTIONS;
  };

  const showSkipLimitToast = () => {
    toast.error(
      getIsStarredMode()
        ? "You can load up to 50 starred questions at a time. Review these before loading more."
        : "You can skip up to 50 questions at a time. Please answer some before loading more.",
      { duration: 4000 },
    );
  };

  const isBlockedBySkipLimit =
    currentQuestionIndex === questions.length - 1 && cannotLoadMoreQuestions();

  useEffect(() => {
    if (questions.length === 0) {
      return;
    }
    if (currentQuestionIndex >= questions.length) {
      return;
    }

    const q = questions[currentQuestionIndex];
    setPracticeQuestionAnswered(
      isSjtQuestion(q)
        ? submittedQuestionIds.has(q.id)
        : isRolePlayQuestion(q)
          ? submittedQuestionIds.has(q.id)
          : selectedAnswers[q.id] !== undefined && submittedQuestionIds.has(q.id),
    );
    const element = document.getElementById(`q${questionNumber}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });

    setHighestQuestionNumberSeen(
      Math.max(highestQuestionNumberSeen, currentQuestionIndex + 1),
    );

    // Initialise SJT ranking to the default A→E order so the student can drag to reorder
    if (isSjtQuestion(q) && !sjtOrderedAnswers[q.id]) {
      setSjtOrderedAnswers((prev) => ({
        ...prev,
        [q.id]: getDefaultSjtOrderIds(q),
      }));
    }
  }, [currentQuestionIndex]);

  // TODO: cover use case of all questions practiced and the student gets initial questions again.
  const getNextBatchOfPracticeQuestions = async (silent?: boolean) => {
    if (isFetchingNext) return;

    const isStarredMode = getIsStarredMode() === true;
    if (
      isStarredMode &&
      numberOfRemainingPracticeQuestionsInModule > 0 &&
      questions.length >= numberOfRemainingPracticeQuestionsInModule
    ) {
      return;
    }

    if (cannotLoadMoreQuestions()) {
      if (!silent) {
        showSkipLimitToast();
      }
      return;
    }

    if (!silent) setIsLoading(true);
    setIsFetchingNext(true);

    try {
      let queryParam =
        getPracticeModuleIds()?.length > 0
          ? `?moduleIds=${getPracticeModuleIds().join(",")}`
          : `?subjectId=${getPracticeSubjectId()}`;

      const examBoards = getExamBoards();
      if (examBoards?.length > 0) {
        queryParam += `&examBoards=${examBoards.join(",")}`;
      }

      if (isStarredMode) {
        queryParam += `&skip=${questions.length}&limit=${STARRED_BATCH_SIZE}`;
      }

      const skippedQuestions = questions.filter(
        (q) => !(q.id in selectedAnswers),
      );

      if (!isStarredMode && skippedQuestions.length > 0) {
        queryParam += `&exclude=${skippedQuestions.map((q) => q.id).join(",")}`;
      }

      const practiceQuestionsResponse =
        (
          isStarredMode
            ? await axiosClient.get(`/starred-questions${queryParam}`)
            : await axiosClient.get(`/questions${queryParam}`)
        ).data;
      const practiceQuestions: Question[] = practiceQuestionsResponse.questions;

      const roomLeft = isStarredMode
        ? MAX_SKIPPED_QUESTIONS - questions.length
        : MAX_SKIPPED_QUESTIONS - getUnansweredQuestionCount();
      const batchToAdd = practiceQuestions.slice(0, Math.max(0, roomLeft));

      // on first call (page load), set the total number of remaining questions in the module
      if (
        practiceQuestions.length > 0 &&
        !numberOfRemainingPracticeQuestionsInModule
      ) {
        if (isStarredMode) {
          // in starred mode, show questions even if previously answered
          setnumberOfRemainingPracticeQuestionsInModule(
            practiceQuestionsResponse.totalNumberOfQuestions
          );

          // this helps ensure all the questions are listed in the sidebar immediately
          setHighestQuestionNumberSeen(
            Math.min(
              practiceQuestionsResponse.totalNumberOfQuestions,
              MAX_SKIPPED_QUESTIONS,
            ),
          );
        } else {
          setnumberOfRemainingPracticeQuestionsInModule(
            practiceQuestionsResponse.totalNumberOfQuestions -
            practiceQuestionsResponse.answeredQuestionsCount,
          );
        }
      }

      setQuestions(questions.concat(batchToAdd));
      // Only reset the prefetch tracker if we actually loaded more questions
      if (batchToAdd.length > 0) {
        setPrefetchedForLength(null);
      }
    } catch (err) {
      if (!silent) {
        setDidFailToLoadQuestions(true);
        throw err;
      }
    } finally {
      if (!silent) setIsLoading(false);
      setIsFetchingNext(false);
    }
  };

  useEffect(() => {
    const getQuestions = async () => {
      try {
        await getNextBatchOfPracticeQuestions();
        setIsLoading(false);
      } catch (err: any) {
        setDidFailToLoadQuestions(true);
        setIsLoading(false);
        if (err?.response?.status !== 429) {
          toast.error("Error loading assignment. Please seek support!");
        }
        console.error(err);
      }
    };
    getQuestions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle case where no questions are returned (all completed)
  useEffect(() => {
    if (didFailToLoadQuestions) return;
    if (!isLoading && questions.length === 0 && numberOfRemainingPracticeQuestionsInModule === 0) {
      const moduleIds = getPracticeModuleIds();

      // If single module selected, redirect to stats page
      if (moduleIds && moduleIds.length === 1) {
        navigate(`/module-stats?moduleIds=${moduleIds[0]}`);
      } else {
        // If multiple modules or all modules selected, show toast and go back
        toast.error("You've completed all these questions, please review or reset your progress", {
          duration: 4000
        });
        navigate(-1); // Go back to previous page
      }
    }
  }, [isLoading, questions.length, numberOfRemainingPracticeQuestionsInModule, didFailToLoadQuestions]);

  // Proactive prefetch when approaching the end of the loaded set
  useEffect(() => {
    if (questions.length === 0) return;
    if (cannotLoadMoreQuestions()) return;
    const batchSize = getIsStarredMode() ? STARRED_BATCH_SIZE : 10;
    const wouldExceedLimit = getIsStarredMode()
      ? questions.length + batchSize > MAX_SKIPPED_QUESTIONS
      : getUnansweredQuestionCount() + batchSize > MAX_SKIPPED_QUESTIONS;
    if (wouldExceedLimit) return;
    // When on the second-to-last item (e.g., 9th when 10 loaded), prefetch silently once per loaded length
    if (
      currentQuestionIndex === questions.length - 2 &&
      prefetchedForLength !== questions.length
    ) {
      setPrefetchedForLength(questions.length);
      getNextBatchOfPracticeQuestions(true);
    }
  }, [currentQuestionIndex, questions.length, prefetchedForLength, submittedQuestionIds]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (shouldIgnoreQuestionNavigationShortcut(e)) {
        return;
      }

      if (e.key === "ArrowLeft") {
        if (currentQuestionIndex > 0) {
          setCurrentQuestionIndex(currentQuestionIndex - 1);
          e.preventDefault();
        }
      } else if (e.key === "ArrowRight") {
        // Prevent navigating past the last question
        if (isAtLastQuestionInModule || isBlockedBySkipLimit) {
          if (isBlockedBySkipLimit) {
            showSkipLimitToast();
          }
          e.preventDefault();
          return;
        }
        // Use existing nextQuestion logic so new batches are fetched when at the end
        e.preventDefault();
        nextQuestion();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentQuestionIndex, questions.length, isAtLastQuestionInModule, isBlockedBySkipLimit]);

  // Check if all questions are completed (but don't auto-redirect)
  const allQuestionsCompleted =
    submittedQuestionIds.size === numberOfRemainingPracticeQuestionsInModule &&
    numberOfRemainingPracticeQuestionsInModule > 0;

  // Show modal when all questions are completed
  useEffect(() => {
    if (allQuestionsCompleted && !showCompletionModal) {
      setShowCompletionModal(true);
    }
  }, [allQuestionsCompleted]);

  const handleShowScores = () => {
    setShowCompletionModal(false);
    const moduleIds = getPracticeModuleIds();
    // If we have module IDs, redirect to stats page with moduleIds
    if (moduleIds && moduleIds.length > 0) {
      navigate(`/module-stats?moduleIds=${moduleIds.join(",")}`);
    }
  };

  // legacy DOM syncing for MCQ selection removed; state now controls MCQ selection

  if (isLoading) {
    return (
      <div className="page-wrapper" id="PracticePage">
        <Sidebar />
        <div className="internal-page-wrapper">Loading...</div>
      </div>
    );
  }

  const nextQuestion = async () => {
    // Guard: do not advance beyond last question in module
    if (isAtLastQuestionInModule) {
      return;
    }
    // Ensure availability when at the last loaded question
    if (currentQuestionIndex === questions.length - 1) {
      if (cannotLoadMoreQuestions()) {
        showSkipLimitToast();
        return;
      }
      await getNextBatchOfPracticeQuestions();
    }
    const nextIdx = currentQuestionIndex + 1;
    if (nextIdx >= questions.length) {
      return;
    }
    setCurrentQuestionIndex(nextIdx);

    const element = document.getElementById(`q${nextIdx}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  function isSjtQuestion(question: Question): boolean {
    return (
      question?.type?.toLowerCase?.() === "sjt" ||
      (Array.isArray(question?.answerOptions) && question.answerOptions.some((a) => typeof a.correctRank === 'number'))
    );
  }

  function isRolePlayQuestion(question: Question): boolean {
    return question?.type?.toLowerCase?.() === "role_play";
  }

  const handleChangeSelectedMcqAnswer = (answerOptionId?: number) => {
    setSelectedAnswers((prev) => {
      const next = { ...prev } as any;
      if (answerOptionId === undefined) {
        delete next[currentQuestion.id];
      } else {
        next[currentQuestion.id] = answerOptionId;
      }
      return next;
    });
  };

  // todo: potentially move this into the title component instead of needing to re-define in each component.
  // instead, just pass in the array of questions instead?
  const handleStarQuestion = async () => {
    try {
      const responseData = await toggleStarQuestion(currentQuestion.id);

      const updatedQuestions = questions.map((q) =>
        q.id === currentQuestion.id
          ? {
            ...q,
            starredBy: responseData.starredQuestion
              ? [responseData.starredQuestion]
              : [],
          }
          : q,
      );

      setQuestions(updatedQuestions);
      toast.success(responseData.message);
    } catch (err) {
      toast.error(
        "Sorry, error occurred starring this question. Please try again or seek support.",
      );
    }
  };

  const setSjtSelection = (questionId: number, newSelection: number[]) => {
    setSjtOrderedAnswers((prev) => ({ ...prev, [questionId]: newSelection }));
  };

  const toggleRolePlayMarkScheme = (questionId: number) => {
    setRolePlayShowMarkScheme((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const recordQuestionSubmit = (payload: {
    questionId: number;
    answerOptionId?: number;
    orderedAnswerOptionIds?: number[];
  }) => {
    axiosClient.post("/question-submit", payload).catch(() => {
      toast.error("Failed to save your answer. Please try again.");
    });
  };

  const handleSjtSubmit = (orderedIds: number[]) => {
    const questionToSubmit = questions[currentQuestionIndex];
    if (
      !orderedIds ||
      orderedIds.length !== questionToSubmit.answerOptions.length
    ) {
      toast.error("Please select all options in order!", { duration: 2000 });
      return;
    }
    const payload = {
      questionId: questionToSubmit.id,
      orderedAnswerOptionIds: orderedIds,
    };
    recordQuestionSubmit(payload);
    questionToSubmit.answerOptions.forEach((answerOption) => {
      const idx = orderedIds.indexOf(answerOption.id);
      if (idx >= 0) {
        (answerOption as any).selectedRank = idx + 1;
      }
    });
    const updatedQuestions = questions.map((q) =>
      q.id === questionToSubmit.id ? questionToSubmit : q,
    );
    setQuestions(updatedQuestions);
    setPracticeQuestionAnswered(true);
    setSubmittedQuestionIds(submittedQuestionIds.add(questionToSubmit.id));
  };

  const handleRolePlaySubmit = (isCorrect: boolean) => {
    const questionToSubmit = questions[currentQuestionIndex];

    // Find the answer option that matches the user's selection
    const selectedAnswerOption = questionToSubmit.answerOptions.find(
      (option) => option.isCorrect === isCorrect
    );

    if (!selectedAnswerOption) {
      toast.error("Error submitting answer. Please try again.");
      return;
    }

    // Submit to backend
    const payload = {
      questionId: questionToSubmit.id,
      answerOptionId: selectedAnswerOption.id,
    };
    recordQuestionSubmit(payload);

    // Mark the selected answer option
    questionToSubmit.answerOptions.forEach((answerOption) => {
      answerOption.selected = answerOption.id === selectedAnswerOption.id;
    });

    const updatedQuestions = questions.map((q) =>
      q.id === questionToSubmit.id ? questionToSubmit : q,
    );
    setQuestions(updatedQuestions);

    // Store the answer
    setRolePlayAnswers((prev) => ({
      ...prev,
      [questionToSubmit.id]: isCorrect,
    }));

    setPracticeQuestionAnswered(true);
    setSubmittedQuestionIds(submittedQuestionIds.add(questionToSubmit.id));
  };

  // TODO: rename this method
  const submitPracticeModeSingleQuestion = () => {
    const questionToSubmit = questions[currentQuestionIndex];
    const isSjt = isSjtQuestion(questionToSubmit);

    // MAKE CALLOUT HERE - submission id should be auto-set as null in the backend. TODO: double-check this??
    if (!isSjt) {
      const selectedAnswerInPracticeMode = selectedAnswers[questionToSubmit.id];

      if (!selectedAnswerInPracticeMode) {
        toast.error("Please select an answer!");
        return;
      }

      const payload = {
        questionId: questionToSubmit.id,
        answerOptionId: selectedAnswerInPracticeMode,
      };
      recordQuestionSubmit(payload);

      questionToSubmit.answerOptions.forEach((answerOption) => {
        answerOption.selected = answerOption.id === selectedAnswerInPracticeMode;
      });
    } else {
      const orderedIds = sjtOrderedAnswers[questionToSubmit.id];
      if (!orderedIds || orderedIds.length !== questionToSubmit.answerOptions.length) {
        toast.error("Please select all options in order!", {
          duration: 2000,
        });
        return;
      }

      const payload = {
        questionId: questionToSubmit.id,
        orderedAnswerOptionIds: orderedIds,
      };
      recordQuestionSubmit(payload);

      // Persist student's selected order for review UI
      questionToSubmit.answerOptions.forEach((answerOption) => {
        const idx = orderedIds.indexOf(answerOption.id);
        if (idx >= 0) {
          (answerOption as any).selectedRank = idx + 1;
        }
      });
    }

    const updatedQuestions = questions.map((q) =>
      q.id === questionToSubmit.id ? questionToSubmit : q,
    );

    console.log("setting questions to: ");
    console.log(updatedQuestions);
    setQuestions(updatedQuestions);

    setPracticeQuestionAnswered(true);
    setSubmittedQuestionIds(submittedQuestionIds.add(questionToSubmit.id));
  };

  const getCurrentQuestion = (): Question => {
    return questions[currentQuestionIndex];
  };

  // If no questions loaded yet, return loading state to prevent crash
  if (questions.length === 0) {
    return (
      <div className="page-wrapper" id="PracticePage">
        <Sidebar />
        <div className="internal-page-wrapper">
          {didFailToLoadQuestions ? "Unable to load questions. Please try again shortly." : "Loading..."}
        </div>
      </div>
    );
  }

  let currentQuestion: Question = getCurrentQuestion();

  const answeredCorrectly = (() => {
    if (currentQuestion?.type?.toLowerCase?.() === "role_play") {
      return rolePlayAnswers[currentQuestion.id] === true;
    }

    if (currentQuestion.type?.toLowerCase?.() === "sjt") {
      return computeSjtCorrect(currentQuestion, sjtOrderedAnswers[currentQuestion.id]);
    }

    return (
      currentQuestion.answerOptions.filter(
        (answerOption) => answerOption.isCorrect && answerOption.selected,
      ).length === 1
    );
  })();

  const delimeters = [
    {
      // NOTE: `display` is by default `true` for the '$$' delimeter option
      // out the box from the react-latex-next package. However, I've set it
      // to `false` to ensure it doesn't render the equation in the centre
      // of the parent. Instead, `false` ensures the equation is rendered
      // in-line with the text. I should also point out that I could have just
      // used the single '$' delimeter option, where all questions with equations
      // have the equation wrapped with a single '$' either side, but I thought
      // this was risky if by accident a question has a $ sign in the question
      // text itself. In such a case, we might not realise, and the question
      // would render incorrectly. Thus, I have opted for the '$$' approach,
      // despite it meaning that we have to manually define the delimeters in
      // our implementation (which wouldn't have been the case otherwise).
      // See: https://www.npmjs.com/package/react-latex-next
      display: false,
      left: "$$",
      right: "$$",
    },
  ];

  const explanationLines = currentQuestion.explanation
    ?.split("\\n")
    .map((line) => (
      <p className="explanation-line">
        <Latex delimiters={delimeters}>{line}</Latex>
      </p>
    ));

  // TODO: rename footer component
  return (
    <div className="page-wrapper" id="PracticePage">
      <Sidebar />
      <div className="internal-page-wrapper">
        {/* <Topbar title={getAssignmentTitle()} /> */}
        <main>
          <div className="inner-main-wrapper row">
            <div
              className="inner-max-width-wrapper col-10"
              style={{
                position: "sticky",
                paddingRight: 0,
                // padding: 0,
              }}
            >
              <div className="top-area">
                {/* rename this component - maybe QuestionMetadata? */}
                <AssignmentPageTitle
                  question={currentQuestion}
                  questionNumber={questionNumber}
                  totalQuestions={numberOfRemainingPracticeQuestionsInModule}
                  isAssignmentMode={false}
                  onNextQuestionClicked={nextQuestion}
                  nextQuestionButtonDisabled={
                    questionNumber === numberOfRemainingPracticeQuestionsInModule ||
                    isBlockedBySkipLimit
                  }
                  currentQuestionIndex={currentQuestionIndex}
                  onPreviousQuestionClicked={() => {
                    setCurrentQuestionIndex(currentQuestionIndex - 1);
                  }}
                  prevQuestionButtonDisabled={questionNumber === 1}
                  //   onSkipQuestionClicked={skipPracticeModeSingleQuestion}
                  handleStarQuestion={handleStarQuestion}
                  leftExtra={
                    isRolePlayQuestion(currentQuestion) && rolePlayShowMarkScheme[currentQuestion.id] ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggleRolePlayMarkScheme(currentQuestion.id)}
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                        }}
                      >
                        Hide Mark Scheme
                      </Button>
                    ) : null
                  }
                  rightExtra={
                    allQuestionsCompleted ? (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleShowScores}
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          padding: "6px 16px"
                        }}
                      >
                        Show Scores →
                      </Button>
                    ) : null
                  }
                />
              </div>
              <div
                id="scroller"
                className={
                  isRolePlayQuestion(currentQuestion) ? "scroller--role-play" : undefined
                }
              >
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    alignItems: "stretch",
                    backgroundColor: isRolePlayQuestion(currentQuestion) ? "white" : "#f8f9fa",
                    padding: "30px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {currentQuestion?.type?.toLowerCase?.() !== "role_play" && (
                      <h5 className="questionTitle" style={{ lineHeight: 1.5 }}>
                        <Latex delimiters={delimeters}>{currentQuestion.text}</Latex>
                      </h5>
                    )}

                    {currentQuestion.imageUrl && (
                      <div
                        style={{
                          marginBottom: "50px",
                        }}
                      >
                        <ZoomImage
                          src={currentQuestion.imageUrl}
                          width={300}
                        ></ZoomImage>
                      </div>
                    )}

                    {/* <div className='questionImage'></div> */}

                    {isRolePlayQuestion(currentQuestion) ? (
                      <RolePlayPracticeBlock
                        question={currentQuestion}
                        onSubmit={handleRolePlaySubmit}
                        isAnswered={practiceQuestionAnswered}
                        showMarkScheme={rolePlayShowMarkScheme[currentQuestion.id] || false}
                        onToggleMarkScheme={() => toggleRolePlayMarkScheme(currentQuestion.id)}
                        selectedAnswer={rolePlayAnswers[currentQuestion.id]}
                        onNextQuestion={nextQuestion}
                        isLastQuestion={isAtLastQuestionInModule || isBlockedBySkipLimit}
                      />
                    ) : isSjtQuestion(currentQuestion) ? (
                      <SjtPracticeBlock
                        question={currentQuestion}
                        practiceQuestionAnswered={practiceQuestionAnswered}
                        selection={sjtOrderedAnswers[currentQuestion.id] || []}
                        delimeters={delimeters}
                        onSelectionChange={(newSel) => setSjtSelection(currentQuestion.id, newSel)}
                        onSubmit={handleSjtSubmit}
                      />
                    ) : (
                      <McqPracticeBlock
                        question={currentQuestion}
                        practiceQuestionAnswered={practiceQuestionAnswered}
                        selectedAnswerId={selectedAnswers[currentQuestion.id]}
                        onChangeSelectedAnswer={handleChangeSelectedMcqAnswer}
                        delimeters={delimeters}
                        onSubmit={submitPracticeModeSingleQuestion}
                      />
                    )}
                  </div>
                  {isSjtQuestion(currentQuestion) ? null : null}
                </div>

                {!isRolePlayQuestion(currentQuestion) && (
                <div id="explanation-container-right-panel">

                  {!practiceQuestionAnswered ? (
                    <div style={{ display: "grid", height: "100%" }}>
                      <div style={{ margin: "auto" }}>
                        <div style={{ display: "grid" }}>
                          <BsLightbulbFill size={34} color="orange" style={{ margin: "auto" }} />
                        </div>
                        <p>
                          {isSjtQuestion(currentQuestion)
                            ? "Submit your ranking to see the explanation"
                            : "Select an answer to see the explanation"}
                        </p>
                      </div>
                    </div>
                  ) : undefined}

                  {practiceQuestionAnswered ? (
                    <div className="practice-explanation-layout">
                      <div
                        className={
                          answeredCorrectly
                            ? "explanation explanation-correct practice-explanation-content"
                            : "explanation explanation-incorrect practice-explanation-content"
                        }
                      >
                        <div className="explanation-title">
                          {answeredCorrectly ? (
                            <span>
                              <IoIosCheckmarkCircle
                                color="green"
                                size={25}
                                style={{
                                  position: "relative",
                                  bottom: "2px",
                                }}
                              />
                              {" "}Correct!
                            </span>
                          ) : (
                            <span>
                              <ImCross
                                color="white"
                                size={20}
                                style={{
                                  backgroundColor: "red",
                                  borderRadius: "10px",
                                  padding: "5px",
                                  position: "relative",
                                  bottom: "2px",
                                }}
                              />
                              {" "}Incorrect!
                            </span>
                          )}
                        </div>

                        {isSjtQuestion(currentQuestion) && (
                          <SjtOrderPanel
                            question={currentQuestion}
                            selection={sjtOrderedAnswers[currentQuestion.id] || []}
                          />
                        )}

                        <div className="explanation-box">
                          <div className="explanation-box-label">Explanation</div>
                          {explanationLines}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 20,
                            marginTop: 20,
                            border: "none",
                            boxShadow: "none",
                          }}
                        >
                          {currentQuestion.explanationImageUrl ? (
                            <ZoomImage
                              src={currentQuestion.explanationImageUrl}
                              width={150}
                            ></ZoomImage>
                          ) : undefined}
                        </div>
                      </div>

                      {!allQuestionsCompleted && (
                        <div className="practice-next-question-footer">
                          <Button onClick={nextQuestion}>
                            Next question
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : undefined}
                </div>
                )}
              </div>
            </div>
            <div className="col-2" style={{ paddingLeft: 0 }}>
              {
                // <PracticePageSidebar
                //   submittedPracticeQuestions={[]}
                //   onSubmittedQuestionClicked={(questionNumber: number) => {
                //     setCurrentQuestionIndex(
                //       questionNumber - 1
                //     );
                //   }}
                //   latestQuestion={questions[0]}
                //   showLatestQuestion={showLatestQuestionInSidebar}
                //   currentQuestionIndex={currentQuestionIndex}
                //   currentQuestionNumber={questionNumber}
                // />

                <PracticePageSidebar
                  currentQuestionIndex={currentQuestionIndex}
                  maxSeenQuestionIndex={highestQuestionNumberSeen - 1}
                  questions={questions}
                  selectedAnswers={selectedAnswers}
                  sjtOrderedAnswers={sjtOrderedAnswers}
                  rolePlayAnswers={rolePlayAnswers}
                  submittedQuestionIds={submittedQuestionIds}
                  onQuestionClicked={setCurrentQuestionIndex}
                  totalQuestionCount={
                    getIsStarredMode()
                      ? numberOfRemainingPracticeQuestionsInModule
                      : undefined
                  }
                  isLoadingMore={getIsStarredMode() ? isFetchingNext : false}
                  sessionLimitReached={cannotLoadMoreQuestions()}
                  sessionQuestionLimit={MAX_SKIPPED_QUESTIONS}
                />
              }
            </div>
          </div>
        </main>

        {/* Completion Modal */}
        <Modal
          show={showCompletionModal}
          onHide={() => setShowCompletionModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
          </Modal.Header>
          <Modal.Body style={{ textAlign: "center", padding: "20px 40px 30px" }}>
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>
              🎉
            </div>
            <h4 style={{ fontWeight: 600, marginBottom: "15px", color: "#20BB27" }}>
              All questions completed!
            </h4>
            <p style={{ fontSize: "16px", color: "#666", marginBottom: 0 }}>
              Click on 'Show Scores' to see how well you did overall.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ border: "none", justifyContent: "center", paddingTop: 0 }}>
            <Button
              variant="secondary"
              onClick={() => setShowCompletionModal(false)}
              style={{ minWidth: "120px" }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default PracticePage;
